import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styled, { css } from "styled-components";
import { ACCENT_100 } from "./colors";
import { boxShadowDs2 } from "./styles";
import useOutsideAlert from "../../hooks/useOutsideAlert";

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: auto;
  background-color: ${({ whiteOverlay }) =>
    whiteOverlay ? "rgba(255, 255, 255, 0.6)" : "rgba(0, 0, 0, 0.6)"};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${({ zIndex }) => zIndex || 100};
  @media screen and (max-width: 768px) {
    align-items: ${({ isMobileBottomSheet }) =>
      isMobileBottomSheet ? "flex-end" : "center"};
  }
`;

const Content = styled.div`
  background-color: ${ACCENT_100};
  border-radius: ${props => props.borderRadius};
  width: ${props => props.width || "100%"};
  max-width: ${props => props.maxWidth};
  min-width: ${props => props.width};
  max-height: ${props => props.maxHeight};
  height: ${props => props.height || "auto"};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: fade-in 0.3s ease-in-out;

  @media screen and (max-width: 768px) {
    width: ${({ mobileWidth }) => mobileWidth || "100vw"};
    min-width: unset;
    max-width: unset;
    max-height: ${({ isMobileBottomSheet }) =>
      isMobileBottomSheet ? "70vh" : "unset"};
    border-radius: ${({ mobileBorderRadius }) => mobileBorderRadius || "unset"};
    overflow: ${({ mobileHeight }) =>
      mobileHeight === "100vh" ? "auto" : "hidden"};
    ${({ mobileHeight }) =>
      mobileHeight
        ? css`
            height: ${mobileHeight};
          `
        : css`
            height: 100vh; // Fallback
            height: 100svh; // Header fix for mobile
          `}
    ${({ isMobileBottomSheet }) =>
      isMobileBottomSheet &&
      css`
        animation: fade-in-from-bottom 0.3s ease-in-out;
        @keyframes fade-in-from-bottom {
          from {
            opacity: 0;
            transform: translateY(100vh);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}
  }

  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(-1.5rem);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  border: ${({ border }) => border || "none"};
  ${({ boxShadow }) => boxShadow && boxShadowDs2};

  * {
    box-sizing: border-box;
  }

  ${({ XXS }) =>
    XXS &&
    css`
      width: ${props => props.width || "25vw"};
      height: ${props => props.height || "auto"};
      max-width: ${props => props.maxWidth || "22.5rem"};
    `}

  ${({ XS }) =>
    XS &&
    css`
      width: ${props => props.width || "30vw"};
      height: ${props => props.height || "60vh"};
      max-width: ${props => props.maxWidth || "30rem"};
      max-height: ${props => props.maxHeight || "30rem"};
    `}

  ${({ S }) =>
    S &&
    css`
      width: ${props => props.width || "40vw"};
      height: ${props => props.height || "80vh"};
      max-width: ${props => props.maxWidth || "40rem"};
      max-height: ${props => props.maxHeight || "40rem"};
    `}

  ${({ M1 }) =>
    M1 &&
    css`
      width: ${props => props.width || "50vw"};
      height: ${props => props.height || "80vh"};
      max-width: ${props => props.maxWidth || "45rem"};
      max-height: ${props => props.maxHeight || "45rem"};
    `}

  ${({ M2 }) =>
    M2 &&
    css`
      width: ${props => props.width || "50vw"};
      height: ${props => props.height || "100vh"};
      max-width: ${props => props.maxWidth || "45rem"};
      max-height: ${props => props.maxHeight || "100vh"};
    `}

  ${({ L }) =>
    L &&
    css`
      width: ${props => props.width || "75vw"};
      height: ${props => props.height || "100vh"};
      max-width: ${props => props.maxWidth || "67.5rem"};
      max-height: ${props => props.maxHeight || "100vh"};
    `}
`;

export const Modal = ({
  XXS,
  XS,
  S,
  M1,
  M2,
  L,
  isMobile,
  togglePopup,
  width,
  height,
  isMobileBottomSheet,
  maxWidth,
  maxHeight,
  mobileWidth,
  mobileHeight,
  noPadding,
  backgroundClickDisabled = false,
  borderRadius,
  mobileBorderRadius,
  children,
  ignoreId = null,
  whiteOverlay,
  boxShadow,
  border,
  zIndex,
  id,
}) => {
  const containerRef = useRef(null);
  useOutsideAlert(
    containerRef,
    !backgroundClickDisabled ? togglePopup : () => {},
    ignoreId
  );

  // this prevents background scrolling
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = "unset");
  }, []);
  return createPortal(
    <Container
      whiteOverlay={whiteOverlay}
      isMobileBottomSheet={isMobileBottomSheet}
      zIndex={zIndex}
    >
      <Content
        id={id}
        XXS={XXS}
        XS={XS}
        S={S}
        M1={M1}
        M2={M2}
        L={L}
        isMobile={isMobile}
        ref={containerRef}
        noPadding={noPadding}
        width={width}
        height={height}
        borderRadius={borderRadius}
        mobileBorderRadius={mobileBorderRadius}
        maxHeight={maxHeight}
        maxWidth={maxWidth}
        mobileHeight={mobileHeight}
        mobileWidth={mobileWidth}
        boxShadow={boxShadow}
        border={border}
        isMobileBottomSheet={isMobileBottomSheet}
      >
        {children}
      </Content>
    </Container>,
    document.getElementById("modalPortal")
  );
};
